/**
 * 69.5, the HQ under fire (lane E; decision 165: a strike squad demolishes the enemy HQ with blasts): what a blast-struck HQ
 * looks like over today's generic scorch (damageForms damageModel), on every builder's shape.
 *
 * - **Breaches** (`breachBuilding`): holes blown in its walls where a ray from outside meets them, at a third of its
 *   height — a dark ragged hole flat to the wall, the wall's broken edges round it, rubble spilled at its foot — `breaches`
 *   of them round the building, from its seed. Each is a smoke source.
 * - **Smoke** (`HqSmoke`): grey puffs rising from each source, drifting downwind, growing and thinning — capped at
 *   `HQ_FALL.puffs` a source and `HQ_FALL.most` an HQ, one instanced draw. The destroyed form smokes from its ruin
 *   (`ruinSmokeAt`), fewer puffs, so a fallen root reads from the diorama by day as its dark lamp does by night.
 *
 * Built in the building's own frame (person units). Presentation only; the smoke steps on the frame's dt.
 */
import * as THREE from 'three';
import { markShared } from './meshBake';
import { DAMAGE_FORM } from './damageForms';

export const HQ_FALL = {
  /** breaches blown in a damaged HQ; their height share of the building, size (person units: width, height) */
  breaches: 3, at: 0.32, size: [2.2, 1.7] as const,
  /** smoke: puffs a source, the most an HQ draws, a puff's life (s), rise (person units a second), its size from → to */
  puffs: 6, most: 18, life: 4.5, rise: 1.6, from: 0.7, to: 2.6,
  /** the destroyed form: smoke sources in the ruin, puffs each */
  ruinSources: 2, ruinPuffs: 5,
  hole: '#141210', smoke: '#86817b', rubble: '#6b6560',
};

const UP = new THREE.Vector3(0, 1, 0);
const hash = (seed: number, i: number, salt: number) => { const v = Math.sin(seed * 12.9898 + i * 78.233 + salt * 37.719) * 43758.5453; return v - Math.floor(v); };
const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}

/** A smoke source in the building's frame: where it leaves and the way out of the wall. */
export interface SmokeSource { at: THREE.Vector3; out: THREE.Vector3 }

/** The building's meshes and box in its own frame. */
function measure(g: THREE.Object3D): { meshes: THREE.Mesh[]; box: THREE.Box3 } {
  g.updateMatrixWorld(true);
  const meshes: THREE.Mesh[] = [], box = new THREE.Box3(), inv = new THREE.Matrix4().copy(g.matrixWorld).invert();
  g.traverse(o => { const m = o as THREE.Mesh; if (m.isMesh && m.visible) { meshes.push(m); box.union(new THREE.Box3().setFromObject(m, true).applyMatrix4(inv)); } });
  return { meshes, box };
}

/**
 * Blow `count` breaches in `g` (a builder's HQ, baked or not, in its own frame): each where a ray toward its middle at
 * HQ_FALL.at of its height first meets a wall. Returns the smoke sources (one a breach).
 */
export function breachBuilding(g: THREE.Group, seed: number, count = HQ_FALL.breaches): SmokeSource[] {
  const { meshes, box } = measure(g), F = HQ_FALL, out: SmokeSource[] = [];
  const c = box.getCenter(new THREE.Vector3()), size = box.getSize(new THREE.Vector3()), reach = Math.max(size.x, size.z) + 4;
  const ray = new THREE.Raycaster(), inv = new THREE.Matrix4().copy(g.matrixWorld).invert(), nm = new THREE.Matrix3();
  const holes = new THREE.Group(); holes.name = 'breaches';
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + hash(seed, i, 1) * 1.2 + 0.4, y = box.min.y + size.y * (F.at + 0.12 * (hash(seed, i, 2) - 0.5));
    const dir = new THREE.Vector3(-Math.cos(a), 0, -Math.sin(a));
    const from = new THREE.Vector3(c.x + Math.cos(a) * reach, y, c.z + Math.sin(a) * reach).applyMatrix4(g.matrixWorld);
    ray.set(from, dir.clone().transformDirection(g.matrixWorld));
    const hit = ray.intersectObjects(meshes, false).find(h => h.face && Math.abs(h.face.normal.y) < 0.9);
    if (!hit || !hit.face) continue;
    const at = hit.point.clone().applyMatrix4(inv);
    const n = hit.face.normal.clone().applyMatrix3(nm.getNormalMatrix(hit.object.matrixWorld)).transformDirection(inv).setY(0);
    if (n.lengthSq() < 1e-6) continue;
    n.normalize();
    if (n.dot(dir) > 0) n.negate();   // (facing out, back along the ray)
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
    const w = F.size[0] * (0.8 + 0.4 * hash(seed, i, 3)), h = F.size[1] * (0.8 + 0.4 * hash(seed, i, 4));
    // the hole: a ragged dark slab just proud of the wall, its broken edges round it, scorched
    const hole = new THREE.Mesh(new THREE.DodecahedronGeometry(0.5, 0), mat(F.hole));
    hole.scale.set(w, h, 0.18); hole.position.copy(at).addScaledVector(n, 0.04); hole.quaternion.copy(q); holes.add(hole);
    for (let k = 0; k < 6; k++) {
      const t = (k / 6) * Math.PI * 2 + hash(seed, i * 7 + k, 5);
      const e = new THREE.Mesh(new THREE.BoxGeometry(0.35 + 0.3 * hash(seed, i * 7 + k, 6), 0.22, 0.2), mat(DAMAGE_FORM.scorch));
      e.position.set(Math.cos(t) * w * 0.52, Math.sin(t) * h * 0.52, 0.08).applyQuaternion(q).add(at);
      e.quaternion.copy(q).multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), t + 0.6 * hash(seed, k, 7)));
      holes.add(e);
    }
    // the rubble blown out at its foot
    for (let k = 0; k < 5; k++) {
      const s = 0.25 + 0.35 * hash(seed, i * 5 + k, 8), side = (hash(seed, i * 5 + k, 9) - 0.5) * w, off = 0.4 + 1.4 * hash(seed, i * 5 + k, 10);
      const r = new THREE.Mesh(new THREE.BoxGeometry(s, s * 0.6, s * 0.9), mat(k % 2 ? F.rubble : DAMAGE_FORM.scorch));
      r.position.copy(at).addScaledVector(n, off).add(new THREE.Vector3(-n.z, 0, n.x).multiplyScalar(side)).setY(box.min.y + s * 0.3);
      r.rotation.set(hash(seed, k, 11) * 0.6, hash(seed, k, 12) * Math.PI, hash(seed, k, 13) * 0.5);
      holes.add(r);
    }
    out.push({ at: at.clone().addScaledVector(n, 0.3).setY(at.y + h * 0.25), out: n });
  }
  g.add(holes);
  return out;
}

/** The destroyed form's smoke: `HQ_FALL.ruinSources` points inside the ruin's box, low, from its seed. */
export function ruinSmokeAt(ruin: THREE.Object3D, seed: number): SmokeSource[] {
  const { box } = measure(ruin), c = box.getCenter(new THREE.Vector3()), s = box.getSize(new THREE.Vector3());
  return Array.from({ length: HQ_FALL.ruinSources }, (_, i) => ({
    at: new THREE.Vector3(c.x + (hash(seed, i, 14) - 0.5) * s.x * 0.5, box.min.y + s.y * 0.4, c.z + (hash(seed, i, 15) - 0.5) * s.z * 0.5),
    out: new THREE.Vector3(0, 1, 0),
  }));
}

/**
 * Smoke rising from an HQ's sources: one instanced draw of at most HQ_FALL.most puffs, each rising, growing and
 * drifting downwind over its life, then starting again at its source (staggered, so the column is steady). `step(dt)`
 * a frame — a few dozen matrix sets.
 */
export class HqSmoke {
  readonly mesh: THREE.InstancedMesh;
  private readonly puffs: Array<{ src: SmokeSource; age: number; spin: number }> = [];
  private readonly m = new THREE.Matrix4(); private readonly p = new THREE.Vector3(); private readonly s = new THREE.Vector3(); private readonly q = new THREE.Quaternion();
  /** downwind (in the building's frame, person units a second) */
  drift = new THREE.Vector3(0.5, 0, 0.2);

  constructor(sources: readonly SmokeSource[], each: number = HQ_FALL.puffs) {
    for (const src of sources) for (let i = 0; i < each && this.puffs.length < HQ_FALL.most; i++) this.puffs.push({ src, age: (i / each) * HQ_FALL.life, spin: i * 1.7 });
    const material = new THREE.MeshLambertMaterial({ color: HQ_FALL.smoke, transparent: true, opacity: 0.42, depthWrite: false });
    this.mesh = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(0.5, 1), material, Math.max(1, this.puffs.length));
    this.mesh.name = 'hqSmoke';
    this.mesh.count = this.puffs.length; this.mesh.frustumCulled = false;
    this.step(0);
  }

  step(dt: number): void {
    const F = HQ_FALL;
    this.puffs.forEach((u, i) => {
      u.age = (u.age + dt) % F.life;
      const k = u.age / F.life, grow = F.from + (F.to - F.from) * Math.sqrt(k), fade = k > 0.75 ? 1 - (k - 0.75) / 0.25 : 1;
      this.p.copy(u.src.at).addScaledVector(u.src.out, 0.6 * Math.min(1, k * 4)).addScaledVector(this.drift, u.age);
      this.p.y += F.rise * u.age;
      this.q.setFromAxisAngle(UP, u.spin + u.age * 0.3);
      this.s.setScalar(Math.max(1e-3, grow * fade));
      this.mesh.setMatrixAt(i, this.m.compose(this.p, this.q, this.s));
    });
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  dispose(): void { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose(); }
}
